import React, { useEffect, useMemo, useState } from "react";

import {
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";

import {
    getYandexMetricaAuthUrl,
    postAuthorizeYandexMetrica,
} from "helpers/destinations/yandexMetrica";

import LoginButton from "components/molecules/LoginButton";

const ACTIVE_STATUS = "Active";

const formatCounterLabel = (counter) => {
    const name = counter?.name || "Counter";
    const site = counter?.site ? ` (${counter.site})` : "";
    return `${name}${site} — ${counter.id}`;
};

const YandexMetricaDestination = ({
    setYandexMetricaData,
    setYandexMetricaAuthenticated,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [counters, setCounters] = useState([]);
    const [partnerInstallID, setPartnerInstallID] = useState(null);
    const [selectedCounterId, setSelectedCounterId] = useState("");

    const activeCounters = useMemo(
        () => counters.filter((counter) => counter?.status === ACTIVE_STATUS),
        [counters],
    );

    const onLoginSuccess = async (data) => {
        setIsLoading(true);
        const response = await postAuthorizeYandexMetrica({
            code: data.code,
            state: data.state,
        });
        setCounters(response?.data?.counters ?? []);
        setPartnerInstallID(response?.data?.partner_install_id ?? null);
        setSelectedCounterId("");
        if (response?.status) {
            setIsAuthenticated(true);
            setYandexMetricaAuthenticated(true);
        } else {
            setIsAuthenticated(false);
            setYandexMetricaAuthenticated(false);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        setYandexMetricaData({
            selectedCounterId: selectedCounterId || null,
            partnerInstallID,
        });
    }, [selectedCounterId, partnerInstallID, setYandexMetricaData]);

    return (
        <div>
            {isAuthenticated &&
                (activeCounters.length > 0 ? (
                    <FormControl fullWidth>
                        <InputLabel id="yandex-metrica-counter-label">
                            Counter
                        </InputLabel>
                        <Select
                            labelId="yandex-metrica-counter-label"
                            label="Counter"
                            value={selectedCounterId}
                            onChange={(event) =>
                                setSelectedCounterId(event.target.value)
                            }
                        >
                            <MenuItem value="" disabled>
                                Select a counter
                            </MenuItem>
                            {activeCounters.map((counter) => (
                                <MenuItem key={counter.id} value={counter.id}>
                                    {formatCounterLabel(counter)}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No active Yandex Metrica counters were found for this
                        account.
                    </Typography>
                ))}
            {!isAuthenticated && (
                <LoginButton
                    code="ymwc"
                    getAuthUrl={() =>
                        getYandexMetricaAuthUrl().then((response) =>
                            response?.status && response?.data?.url
                                ? response.data.url
                                : null,
                        )
                    }
                    onAuthUrlError={() => setIsLoading(false)}
                    paramNames={["code", "state"]}
                    requireAllParams
                    onSuccess={onLoginSuccess}
                    dataTransform={(params) => ({
                        code: params.code,
                        state: params.state,
                    })}
                    logoSrc="/assets/images/yandex.png"
                    buttonText="Connect Yandex Metrica"
                    disabled={false}
                    isLoadingExternal={isLoading}
                />
            )}
        </div>
    );
};

export default YandexMetricaDestination;
